// The two badges an alert row carries, deliberately DIFFERENT SHAPES so they
// never read as one thing:
//
//  · PLATFORM (Meta / Google) — a small SQUARE-cornered solid tint. "Where".
//  · TYPE (No spend / CPL / Lead drought / Pacing) — a PILL with a border.
//    "What kind". Its hue hints at the type's character, but ⚠️ SEVERITY IS NOT
//    ENCODED HERE — severity is owned by the row's red/amber dot, and a second
//    colour scale saying the same thing would compete with it (a red "No spend"
//    pill beside an amber dot would be actively misleading).
//
// Shared by the alerts panel and the book table's status popover, so an alert
// looks the same wherever it is shown.

import { cn } from '../../lib/utils';
import type { AlertType } from '../../lib/advertisingHome/alerts';
import { alertTypeLabel } from '../../lib/advertisingHome/alertsView';
import type { AdChannel } from '../../types/advertisingHome';

export function ChannelChip({ channel }: { channel: AdChannel }): React.JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        channel === 'meta' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700',
      )}
    >
      {channel === 'meta' ? 'Meta' : 'Google'}
    </span>
  );
}

// Hue = the type's character (money-out problems warm, lead problems cool),
// all at the same low saturation so none of them shouts louder than the dot.
const TYPE_CLASS: Record<AlertType, string> = {
  no_spend: 'border-slate-300 bg-slate-50 text-slate-700',
  spend_no_leads: 'border-red-200 bg-red-50 text-red-800',
  cpl_spike: 'border-orange-200 bg-orange-50 text-orange-800',
  lead_drought: 'border-violet-200 bg-violet-50 text-violet-800',
  pacing: 'border-sky-200 bg-sky-50 text-sky-800',
};

export function AlertTypeBadge({ type }: { type: AlertType }): React.JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-medium',
        TYPE_CLASS[type],
      )}
    >
      {alertTypeLabel(type)}
    </span>
  );
}

export function SeverityDot({
  severity,
  className,
}: {
  severity: 'red' | 'amber';
  className?: string;
}): React.JSX.Element {
  return (
    <span
      className={cn(
        'h-2.5 w-2.5 shrink-0 rounded-full',
        severity === 'red' ? 'bg-red-500' : 'bg-amber-500',
        className,
      )}
      title={severity === 'red' ? 'Critical' : 'Needs attention'}
      aria-label={severity === 'red' ? 'Critical' : 'Needs attention'}
    />
  );
}
